import { useState } from "react";
import { ABOUT_NOT_ADMIN, EMPTY_NOT_ADMIN, SYNC_ADMIN_ONLY } from "../lib/accounts-copy.ts";
import type { Standing, XAccount } from "../lib/api.ts";
import { api } from "../lib/api.ts";
import { accountCurrencyText, approvalStatus } from "../lib/format.ts";
import { isPlatformAdmin } from "../lib/roles.ts";
import { useLoad } from "../lib/useLoad.ts";
import { Badge, Button, InfoBanner, Page, Table } from "../ui/index.ts";
import { badgeVariant } from "./Overview.tsx";

export function Accounts({ standing }: { standing: Standing }) {
	const [accounts, reload] = useLoad(() => api.xAccounts());
	const [busy, setBusy] = useState(false);
	// A failed sync and a successful one are not the same news. Both used to land
	// in one `note` rendered as an info banner, so "Meta API error 190: token
	// expired" arrived looking exactly like "Pulled 4 accounts". That was fbads,
	// where this screen came from; an expired X token would arrive the same way.
	const [note, setNote] = useState<{ text: string; failed: boolean } | null>(null);

	async function sync() {
		setBusy(true);
		setNote(null);
		try {
			const { synced } = await api.syncXAccounts();
			setNote({
				text: `Pulled ${synced} account${synced === 1 ? "" : "s"} from X.`,
				failed: false,
			});
			reload();
		} catch (e) {
			setNote({ text: e instanceof Error ? e.message : String(e), failed: true });
		} finally {
			setBusy(false);
		}
	}

	const list = accounts.state === "ready" ? accounts.data.accounts : [];

	return (
		<Page
			title="Ad accounts"
			description={
				isPlatformAdmin(standing)
					? "Read from this console's own database, not from X on every load. Pull a refresh when accounts change in X Ads Manager."
					: ABOUT_NOT_ADMIN
			}
			actions={
				isPlatformAdmin(standing) ? (
					<Button onClick={() => void sync()} loading={busy}>
						Sync from X
					</Button>
				) : (
					<p className="max-w-sm text-sm text-muted">{SYNC_ADMIN_ONLY}</p>
				)
			}
		>
			{note && <InfoBanner variant={note.failed ? "danger" : "info"} title={note.text} />}
			{accounts.state === "ready" && accounts.data.truncated && (
				<InfoBanner variant="warning" title="This list is not complete.">
					More rows exist than one read returns, so what you see here is drawn from the first 1000
					by name. Nothing is missing from the database — the rest is not read until the read itself
					is paged.
				</InfoBanner>
			)}
			{accounts.state === "failed" && (
				<InfoBanner variant="danger" title="Accounts could not be read.">
					{accounts.why}
				</InfoBanner>
			)}

			<Table<XAccount>
				columns={[
					{
						id: "name",
						header: "Account",
						accessor: (a) => (
							<>
								<div className="font-medium text-content">{a.name}</div>
								{a.businessName && <div className="text-xs text-muted">{a.businessName}</div>}
								<div className="font-mono text-xs text-muted">{a.id}</div>
							</>
						),
					},
					{
						id: "x",
						header: "X account id",
						accessor: (a) => <span className="font-mono text-xs">{a.xAccountId}</span>,
					},
					{ id: "currency", header: "Currency", accessor: (a) => accountCurrencyText(a.currency) },
					{ id: "timezone", header: "Time zone", accessor: (a) => a.timezone ?? "—" },
					{
						id: "state",
						header: "State",
						accessor: (a) => {
							const st = approvalStatus(a.approvalStatus);
							return <Badge variant={badgeVariant(st.tone)}>{st.label}</Badge>;
						},
					},
					{
						id: "synced",
						header: "Synced",
						accessor: (a) => (
							<span className="text-muted">
								{a.syncedAt ? new Date(a.syncedAt).toLocaleString() : "never"}
							</span>
						),
					},
				]}
				data={list}
				keyExtractor={(a) => a.id}
				failed={accounts.state === "failed"}
				loading={accounts.state === "loading"}
				emptyMessage={
					isPlatformAdmin(standing)
						? "Nothing synced yet. Once X Ads API access is approved and credentials are configured, pull a refresh and the accounts they can reach appear here."
						: EMPTY_NOT_ADMIN
				}
			/>
		</Page>
	);
}
